import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Box,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    useTheme,
    TextField,
    Switch,
    FormControlLabel,
    IconButton,
    Button,
    Paper,
    Divider,
    Tooltip
} from '@mui/material';
import {
    ViewCarousel,
    Save,
    ExpandMore,
    Add,
    Delete,
    DragIndicator,
    LocalShipping,
    Checkroom,
    Article
} from '@mui/icons-material';
import { PortalScreenHeader } from '../../../../components/PortalScreenHeader';
import { PermissionWrapper } from '../../../../components/PermissionWrapper';
import { BasicButton } from '../../../../components/BasicButton';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';
import apiClient from '../../../../services/api/ApiClient';
import { ProductDetailSection } from '../../../../services/config.service';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableSectionProps {
    section: ProductDetailSection;
    isExpanded: boolean;
    onToggleExpand: () => void;
    onUpdate: (id: string, updates: Partial<ProductDetailSection>) => void;
    onDelete: (id: string) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
    section,
    isExpanded,
    onToggleExpand,
    onUpdate,
    onDelete
}) => {
    const theme = useTheme();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: section.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const getSectionIcon = (id: string) => {
        switch (id) {
            case 'shipping':
                return <LocalShipping fontSize='small' />;
            case 'care':
                return <Checkroom fontSize='small' />;
            default:
                return <Article fontSize='small' />;
        }
    };

    return (
        <Box ref={setNodeRef} style={style}>
            <Accordion
                expanded={isExpanded}
                onChange={onToggleExpand}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    mb: 2,
                    overflow: 'hidden',
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                        minHeight: { xs: 56, sm: 64 },
                        '& .MuiAccordionSummary-content': {
                            my: { xs: 1.5, sm: 2 },
                            alignItems: 'center',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <IconButton
                            {...attributes}
                            {...listeners}
                            size='small'
                            sx={{
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                color: theme.palette.text.secondary
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <DragIndicator />
                        </IconButton>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                backgroundColor: section.isEnabled
                                    ? theme.palette.primary.main + '15'
                                    : theme.palette.action.disabledBackground,
                                color: section.isEnabled
                                    ? theme.palette.primary.main
                                    : theme.palette.text.disabled,
                            }}
                        >
                            {getSectionIcon(section.id)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant='subtitle1'
                                fontWeight={600}
                                sx={{
                                    lineHeight: 1.3,
                                    textAlign: 'left',
                                    color: section.isEnabled ? 'text.primary' : 'text.disabled'
                                }}
                            >
                                {section.title}
                            </Typography>
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{
                                    textAlign: 'left',
                                    display: { xs: 'none', sm: 'block' },
                                    opacity: section.isEnabled ? 1 : 0.6
                                }}
                            >
                                {section.isDefault ? 'Default section' : 'Custom section'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {section.isEnabled && (
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.success.main + '20',
                                        color: theme.palette.success.main,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        display: { xs: 'none', sm: 'block' }
                                    }}
                                >
                                    ENABLED
                                </Box>
                            )}
                            {!section.isDefault && (
                                <Tooltip title='Delete section'>
                                    <IconButton
                                        size='small'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(section.id);
                                        }}
                                        sx={{ color: theme.palette.error.main }}
                                    >
                                        <Delete fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Title Field */}
                        <TextField
                            label='Section Title'
                            value={section.title}
                            onChange={(e) => onUpdate(section.id, { title: e.target.value })}
                            fullWidth
                            size='small'
                            placeholder='e.g., Shipping & Returns'
                        />

                        {/* Content Field */}
                        <TextField
                            label='Section Content'
                            value={section.content.replace(/<[^>]*>/g, '\n').replace(/\n+/g, '\n').trim()}
                            onChange={(e) => {
                                // Convert line breaks to paragraph tags
                                const lines = e.target.value.split('\n').filter(line => line.trim());
                                const htmlContent = lines.map(line => `<p>${line}</p>`).join('');
                                onUpdate(section.id, { content: htmlContent });
                            }}
                            fullWidth
                            multiline
                            rows={4}
                            size='small'
                            placeholder='Enter the content that will be displayed when this section is expanded...'
                            helperText='Each line will be displayed as a separate paragraph'
                        />

                        {/* Enable/Disable Toggle */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={section.isEnabled}
                                    onChange={(e) => onUpdate(section.id, { isEnabled: e.target.checked })}
                                    color='primary'
                                />
                            }
                            label={
                                <Typography variant='body2'>
                                    {section.isEnabled ? 'Section is visible on product pages' : 'Section is hidden from product pages'}
                                </Typography>
                            }
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export const ProductDetailSettings: React.FC = () => {
    const theme = useTheme();
    const { reloadConfig } = useStoreConfig();

    const [sections, setSections] = useState<ProductDetailSection[]>([]);
    const [originalSections, setOriginalSections] = useState<ProductDetailSection[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Load sections on mount
    useEffect(() => {
        const loadSections = async () => {
            try {
                const response = await apiClient.get<{ sections: ProductDetailSection[] }>('/customization/product-detail');
                const loadedSections = response.data.sections || [];
                setSections(loadedSections);
                setOriginalSections(loadedSections);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load sections');
            } finally {
                setLoading(false);
            }
        };

        loadSections();
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setSections((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                const newItems = arrayMove(items, oldIndex, newIndex);
                // Update sort orders
                return newItems.map((item, index) => ({ ...item, sortOrder: index + 1 }));
            });
        }
    }, []);

    const handleToggleExpand = useCallback((sectionId: string) => {
        setExpandedPanels(prev =>
            prev.includes(sectionId)
                ? prev.filter(p => p !== sectionId)
                : [...prev, sectionId]
        );
    }, []);

    const handleUpdateSection = useCallback((id: string, updates: Partial<ProductDetailSection>) => {
        setSections(prev =>
            prev.map(section =>
                section.id === id ? { ...section, ...updates } : section
            )
        );
    }, []);

    const handleDeleteSection = useCallback((id: string) => {
        setSections(prev => prev.filter(section => section.id !== id));
        setExpandedPanels(prev => prev.filter(p => p !== id));
    }, []);

    const handleAddSection = useCallback(() => {
        const newSection: ProductDetailSection = {
            id: `custom-${Date.now()}`,
            title: 'New Section',
            content: '<p>Add your content here...</p>',
            isEnabled: true,
            sortOrder: sections.length + 1,
            isDefault: false
        };
        setSections(prev => [...prev, newSection]);
        setExpandedPanels(prev => [...prev, newSection.id]);
    }, [sections.length]);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            await apiClient.patch('/customization/product-detail', { sections });
            setSuccess(true);
            setOriginalSections(sections);
            await reloadConfig();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save sections');
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(sections) !== JSON.stringify(originalSections);

    if (loading) {
        return (
            <PermissionWrapper screen='product-detail' permission='view'>
                <Container maxWidth='xl' sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Container>
            </PermissionWrapper>
        );
    }

    return (
        <PermissionWrapper screen='product-detail' permission='view'>
            <Container
                component='main'
                maxWidth='xl'
                sx={{
                    height: '100%',
                    paddingX: { xs: 0, md: 2 },
                    paddingBottom: 4
                }}
            >
                <PortalScreenHeader
                    title='Product Detail'
                    subtitle='Customize the expandable sections shown on product pages'
                    icon={<ViewCarousel />}
                    mobileActionIcon={saving ? <CircularProgress size={20} color='inherit' /> : <Save />}
                    onMobileActionClick={hasChanges && !saving ? handleSave : undefined}
                    actions={
                        <BasicButton
                            title={saving ? 'Saving...' : 'Save'}
                            onClick={handleSave}
                            disabled={saving || !hasChanges}
                            startIcon={saving ? <CircularProgress size={20} color='inherit' /> : <Save />}
                        />
                    }
                />

                {error && (
                    <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                        Product detail sections updated successfully!
                    </Alert>
                )}

                {/* Preview Info */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2
                    }}
                >
                    <Typography variant='body2' color='text.secondary'>
                        These sections appear as expandable accordions on your product detail pages.
                        Drag to reorder, toggle visibility, or add custom sections with your own content.
                    </Typography>
                </Paper>

                {/* Sections List */}
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={sections.map(s => s.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {sections.map((section) => (
                            <SortableSection
                                key={section.id}
                                section={section}
                                isExpanded={expandedPanels.includes(section.id)}
                                onToggleExpand={() => handleToggleExpand(section.id)}
                                onUpdate={handleUpdateSection}
                                onDelete={handleDeleteSection}
                            />
                        ))}
                    </SortableContext>
                </DndContext>

                {/* Add Section Button */}
                <Button
                    variant='outlined'
                    startIcon={<Add />}
                    onClick={handleAddSection}
                    fullWidth
                    sx={{
                        py: 1.5,
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        '&:hover': {
                            borderStyle: 'dashed',
                            borderWidth: 2
                        }
                    }}
                >
                    Add Custom Section
                </Button>
            </Container>
        </PermissionWrapper>
    );
};

export default ProductDetailSettings;
